/* eslint-disable max-len */
import axios from 'axios';
import {
  ContentData, IUploadConfig,
  UploadRequestMetaData, UploadResponse, UploadScenarios,
} from 'interfaces/file-uploader';
import { IRestApiResponse } from 'interfaces';
import { FileExtensions } from 'config';
import pdfDocument from '../assets/images/pdf-document.png';
import powerpointDocument from '../assets/images/powerpoint-document.png';
import excelDocument from '../assets/images/excel-document.png';
import csvDocument from '../assets/images/csv-document.png';
import { uniqueIdGenerate } from './data-utils';
import { request } from './request';
import store from '../store';

const mbSize = 1000000;

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FOLDER = 'folder',
  FONT = 'font',
  COMPRESSED_FILE = 'compressed-file',
}

export const videoExtensions = [
  FileExtensions.mp4,
  FileExtensions.flv,
  FileExtensions.mkv,
  FileExtensions.webm,
  FileExtensions.mov,
  FileExtensions.wmv,
];
export const imageExtensions = [FileExtensions.jpg, FileExtensions.jpeg,
  FileExtensions.png, FileExtensions.gif];
export const documentExtensions = [
  FileExtensions.pdf,
  FileExtensions.ppt,
  FileExtensions.pptx,
  FileExtensions.xls,
  FileExtensions.xlsx,
  FileExtensions.csv,
];
export const fontExtensions = [FileExtensions.woff, FileExtensions.woff2];
export const compressItemsExtensions = [FileExtensions.zip];

export const uploadConfig = new Map<number, IUploadConfig>([
  [UploadScenarios.entFontUpload, { allowedExtensions: [...fontExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.expertVideo, { allowedExtensions: [...videoExtensions], sizeLimit: 5120 * mbSize }],
  [UploadScenarios.entLogo, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.entSponsor, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.entPromotional, { allowedExtensions: [...imageExtensions, ...videoExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.entPromotional_2, { allowedExtensions: [...imageExtensions, ...videoExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.adminProfile, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.expertProfile, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.expertSponsor, { allowedExtensions: [...imageExtensions], sizeLimit: 500 * mbSize }],
  [UploadScenarios.expertBadge, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.expertContent, {
    allowedExtensions: [...imageExtensions, ...videoExtensions, ...documentExtensions],
    sizeLimit: 5120 * mbSize,
    sizeLimitsByType: {
      [FileType.DOCUMENT]: 30 * mbSize,
    },
  }],
  [UploadScenarios.articleBanner, { allowedExtensions: [...imageExtensions], sizeLimit: 100 * mbSize }],
  [UploadScenarios.sessionBanner, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.groupBanner, { allowedExtensions: [...imageExtensions], sizeLimit: 100 * mbSize }],
  [UploadScenarios.formBadge, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.scorm, { allowedExtensions: [FileExtensions.zip], sizeLimit: 500 * mbSize }],
  [UploadScenarios.ctaBackground, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.textBlock, { allowedExtensions: [...imageExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.sessionHighlightMedia, { allowedExtensions: [...imageExtensions, ...videoExtensions], sizeLimit: 30 * mbSize }],
  [UploadScenarios.waitingRoomMedia, { allowedExtensions: [...imageExtensions, ...videoExtensions], sizeLimit: 5120 * mbSize }],
  [UploadScenarios.FormMedia, { allowedExtensions: [...imageExtensions, ...videoExtensions], sizeLimit: 5120 * mbSize }],
]);

export const extensionToType = new Map<string, string>([

]);
videoExtensions.forEach((video) => { extensionToType.set(video, 'video'); });
imageExtensions.forEach((image) => { extensionToType.set(image, 'image'); });
documentExtensions.forEach((doc) => { extensionToType.set(doc, FileType.DOCUMENT); });
fontExtensions.forEach((font) => { extensionToType.set(font, 'font'); });
compressItemsExtensions.forEach((item) => {
  extensionToType.set(item, FileType.COMPRESSED_FILE);
});
/**
 * @param  {any} e  File change event
 * @param  {string} url Current url of the content data object
 * @return UploadResponse
 */
const handleContentDataUpload = async (
  e: any,
  url: string,
  scenario: UploadScenarios,
  onUploadProgress?: (event: any) => void,
): Promise<UploadResponse> => {
  try {
    const state = store.getState();
    const fileUploaded = e.target.files[0] || e.dataTransfer.files[0];
    let action = '';
    const content: ContentData = {
      action: '', type: '', file: '', fileName: '', url, extension: '',
    };
    const UPLOAD_PATH: UploadRequestMetaData = { path: '/pre-signed-url/create-url', method: 'POST' };
    const config = uploadConfig.get(scenario);
    if (!config) {
      return {
        name: '', url, extension: '', statusCode: 400, message: 'Invalid configuration', category: '',
      };
    }

    if (fileUploaded) {
      if (url === '') {
        action = 'new';
      } else {
        action = 'update';
      }
      action = 'new';
      content.file = fileUploaded;
      content.action = action;
      content.extension = fileUploaded.name.split('.').pop().toLowerCase();
      content.fileName = `${uniqueIdGenerate()}`;

      if (!config.allowedExtensions.includes(`.${content.extension}`)) {
        return {
          name: '', url, extension: '', statusCode: 400, message: `Only ${config.allowedExtensions.join(', ')} file extensions are allowed`, category: '',
        };
      }

      content.type = extensionToType.get(`.${content.extension}`) as string;

      const applicableSizeLimit = config.sizeLimitsByType?.[content.type] ?? config.sizeLimit;

      if (applicableSizeLimit < content.file.size) {
        return {
          name: '',
          url,
          extension: '',
          statusCode: 400,
          message: `File is larger than the recommended size ${applicableSizeLimit / mbSize} MB`,
          category: '',
        };
      }
      const fileData: {
        fileName: string;
        fileSize: number;
        tenantId: string;
      } = {
        fileName: fileUploaded.name,
        fileSize: fileUploaded.size,
        tenantId: state['feature/ent-acc/edit-ent-acc'].id || state['feature/ent-acc/onboard-ent-acc'].id,
      };

      try {
        const res: IRestApiResponse = await request(UPLOAD_PATH, fileData, true, false);
        if (res.statusCode === 201) {
          const {
            uniqueFileName, presignedPost, contentType, publicUrl, acl, fileCategory,
          } = res.data;
          const formData = {
            ...presignedPost.fields,
            'Content-Type': contentType,
            acl,
            file: fileUploaded,
          };
          await axios.post(presignedPost.url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
          });

          return {
            name: uniqueFileName,
            url: res.statusCode === 201 ? publicUrl : '',
            extension: content.extension,
            category: fileCategory,
            fileData: {
              fileName: fileData.fileName,
              size: fileData.fileSize,
            },
            statusCode: res.statusCode,
            message: res.message,
          };
        }
      } catch (error) {
        return {
          name: content.fileName,
          url,
          extension: content.extension,
          category: content.type,
          fileData: {
            fileName: fileData.fileName,
            size: fileData.fileSize,
          },
          statusCode: 400,
          message: (error as Error).message,
        };
      }
    }
    return {
      name: content.fileName, url, extension: content.extension, category: content.type, statusCode: 400, message: '',
    };
  } catch (error) {
    return {
      name: '', url, extension: '', statusCode: 400, message: '', category: '',
    };
  }
};

/**
 * @param  {any} e  File change event
 * @param  {string} url Current url of the content data object that needs to be deleted
 * @return void
 */
export const handleContentDataDelete = async (url: string, scenario: UploadScenarios)
: Promise<void> => {
  const content: ContentData = {
    action: 'delete', type: '', file: '', fileName: '', url, extension: '',
  };

  if (!url || !scenario) {
    return;
  }

  const UPLOAD_PATH: UploadRequestMetaData = { path: '', method: 'POST' };
  if (scenario === UploadScenarios.entSponsor
    || scenario === UploadScenarios.entPromotional
    || scenario === UploadScenarios.expertSponsor
    || scenario === UploadScenarios.expertProfile
    || scenario === UploadScenarios.expertVideo) {
    UPLOAD_PATH.path = '/ent-account/upload';
  } else {
    return;
  }
  await request(UPLOAD_PATH, content, true, true);
};

/**
 * Get the appropriate document icon based on file extension
 * @param fileExtension - File extension (e.g., '.pdf', '.pptx', '.xlsx')
 * @returns Icon source path
 */
export const getDocumentIcon = (fileExtension: string): string => {
  const ext = fileExtension.toLowerCase();
  const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;

  switch (normalizedExt) {
    case '.pdf':
      return pdfDocument;
    case '.ppt':
    case '.pptx':
      return powerpointDocument;
    case '.xls':
    case '.xlsx':
      return excelDocument;
    case '.csv':
      return csvDocument;
    default:
      return pdfDocument;
  }
};

/**
 * Get document type name for display
 * @param fileExtension - File extension (e.g., '.pdf', '.pptx', '.xlsx')
 * @returns Human readable document type
 */
export const getDocumentTypeName = (fileExtension: string): string => {
  const ext = fileExtension.toLowerCase();
  const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;

  switch (normalizedExt) {
    case '.pdf':
      return 'PDF Document';
    case '.ppt':
      return 'PowerPoint Presentation';
    case '.pptx':
      return 'PowerPoint Presentation';
    case '.xls':
      return 'Excel Spreadsheet';
    case '.xlsx':
      return 'Excel Spreadsheet';
    case '.csv':
      return 'CSV Spreadsheet';
    default:
      return 'Document';
  }
};

export default handleContentDataUpload;
